import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Package, IndianRupee } from 'lucide-react';

import {
  Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, FactBand, Fact, CellButton,
} from '@/components/data';

import { DEPT_NAMES, canSeeFinancials as canSeeOrderMoney, canSeeCustomerMoney } from '@/lib/constants';
import { ON_ACCOUNT } from '@/lib/payments';

import { STATUS_COLORS } from '@/lib/status-colors';
import PhoneCustomerProfile from './Customers/PhoneCustomerProfile';
import RecordPaymentPane from '@/components/RecordPaymentPane';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useRecordPayment } from '@/hooks/useRecordPayment';

export default function CustomerProfilePage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  
  // Check if user can see financial data
  // the lifetime ledger is a narrower gate than recording one payment against
  // one order, which the city office does in the field. Both read the whole
  // roles array — a second role that grants this must not be denied by the first
  const canSeeFinancials = canSeeCustomerMoney(user);
  const canRecordPayments = canSeeOrderMoney(user);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customerId}/profile`);
      setProfile(res.data);
    } catch (err) {
      toast.error('Failed to load customer profile');
      navigate('/customers');
    }
    setLoading(false);
  }, [customerId, navigate]);

  const fetchOrders = useCallback(async () => {
    try {
      const params = { page: ordersPage, limit: 15 };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (paymentStatusFilter && paymentStatusFilter !== 'all') params.payment_status = paymentStatusFilter;
      const res = await api.get(`/customers/${customerId}/orders-list`, { params });
      setOrders(res.data.orders);
      setOrdersTotal(res.data.total);
    } catch (err) {
    }
  }, [customerId, ordersPage, statusFilter, paymentStatusFilter]);

  const fetchPayments = useCallback(async () => {
    if (!canSeeFinancials) return;
    try {
      const res = await api.get(`/customers/${customerId}/payments-list`, { params: { page: paymentsPage, limit: 15 } });
      setPayments(res.data.payments);
      setPaymentsTotal(res.data.total);
    } catch (err) {
    }
  }, [customerId, paymentsPage, canSeeFinancials]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (profile) fetchOrders();
  }, [fetchOrders, profile]);

  // the payments rail is always on screen now, so it always loads
  useEffect(() => {
    if (profile) fetchPayments();
  }, [fetchPayments, profile]);

  const payment = useRecordPayment({
    onRecorded: () => { fetchProfile(); fetchOrders(); fetchPayments(); },
  });

  // the ledger is already on screen, so the form is handed it rather than
  // fetching the same profile a second time
  const openPaymentDialog = (orderId = ON_ACCOUNT) => {
    if (!profile) return;
    payment.open(profile.customer, orderId, {
      orders: profile.outstanding_orders || [],
      outstanding: profile.financial_summary?.outstanding,
      advance: profile.financial_summary?.advance_balance,
    });
  };

  const formatDate = (dateStr) => formatIST(dateStr, { dateOnly: true });

  const formatDateTime = (dateStr) => formatIST(dateStr, { compact: true });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (!profile) return null;

  const { customer, financial_summary, outstanding_orders, aging_summary, recent_payments, customer_since, total_orders } = profile;
  const ordersPages = Math.ceil(ordersTotal / 15);
  const paymentsPages = Math.ceil(paymentsTotal / 15);

  const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

  const orderColumns = [
    {
      key: 'order_id',
      header: 'Order',
      width: '10rem',
      render: (o) => (
        <>
          <span className="block truncate font-semibold text-brand-blue">{o.order_id}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{formatDate(o.created_at)}</span>
        </>
      ),
    },
    {
      key: 'products',
      header: 'Items',
      width: 'minmax(12rem,1.5fr)',
      render: (o) => (
        <span className="flex flex-wrap gap-1">
          {(o.line_items || []).slice(0, 3).map((item, i) => (
            <span key={i} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              {DEPT_NAMES[item.order_type] || item.order_type}{item.quantity ? ` ×${item.quantity}` : ''}
            </span>
          ))}
          {(o.line_items?.length || 0) > 3 && (
            <span className="text-[10px] text-slate-400">+{o.line_items.length - 3}</span>
          )}
          {!o.line_items?.length && <span className="text-slate-400">—</span>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '8rem',
      render: (o) => (
        <Badge className={`text-[10px] ${STATUS_COLORS[o.status]?.class || ''}`}>
          {STATUS_COLORS[o.status]?.label || o.status}
        </Badge>
      ),
    },
    canSeeFinancials && {
      key: 'order_value',
      header: 'Value',
      align: 'right',
      width: '7.5rem',
      render: (o) => <span className="text-slate-700">{money(o.order_value)}</span>,
      total: (rows) => money(rows.reduce((s, o) => s + (o.order_value || 0), 0)),
    },
    canSeeFinancials && {
      key: 'total_paid',
      header: 'Paid',
      align: 'right',
      width: '7.5rem',
      render: (o) => <span className="text-emerald-600">{money(o.total_paid)}</span>,
      total: (rows) => money(rows.reduce((s, o) => s + (o.total_paid || 0), 0)),
    },
    canSeeFinancials && {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      width: '7.5rem',
      render: (o) => {
        const balance = (o.order_value || 0) - (o.total_paid || 0);
        return (
          <span className={`font-medium ${balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {money(balance)}
          </span>
        );
      },
      total: (rows) => money(rows.reduce((s, o) => s + ((o.order_value || 0) - (o.total_paid || 0)), 0)),
    },
    canSeeFinancials && {
      key: 'payment_status',
      header: 'Payment',
      width: '7rem',
      render: (o) => (
        <span className={`text-[11px] font-medium ${
          o.payment_status === 'fully_paid' ? 'text-emerald-600'
            : o.payment_status === 'partially_paid' ? 'text-amber-600' : 'text-red-600'
        }`}>
          {(o.payment_status || 'unpaid').replace('_', ' ')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '6rem',
      align: 'right',
      render: (o) => (
        <span className="flex items-center justify-end gap-0.5">
          {canRecordPayments && (o.order_value || 0) - (o.total_paid || 0) > 0 && (
            <CellButton icon={IndianRupee} label="Record payment" tone="good"
              onClick={() => openPaymentDialog(o.id)} />
          )}
          <CellButton icon={Package} label="Open order" iconOnly onClick={() => navigate(`/orders/${o.id}`)} />
        </span>
      ),
    },
  ].filter(Boolean);

  const aging = aging_summary || {};
  const paymentList = payments.length ? payments : (recent_payments || []);

  // one column of cards below lg, from the same state and the same handlers
  if (isMobile) {
    return (
      <PhoneCustomerProfile
        profile={profile}
        canSeeFinancials={canSeeFinancials}
        canRecordPayments={canRecordPayments}
        orders={orders} ordersTotal={ordersTotal}
        ordersPage={ordersPage} ordersPages={ordersPages} onOrdersPage={setOrdersPage}
        statusFilter={statusFilter}
        onStatus={(v) => { setStatusFilter(v); setOrdersPage(1); }}
        paymentStatusFilter={paymentStatusFilter}
        onPaymentStatus={(key) => { setPaymentStatusFilter(!key || key === 'all' ? 'all' : key); setOrdersPage(1); }}
        payments={paymentList} paymentsTotal={paymentsTotal}
        paymentsPage={paymentsPage} paymentsPages={paymentsPages} onPaymentsPage={setPaymentsPage}
        payment={payment}
        onBack={() => navigate('/customers')}
        onRecordPayment={openPaymentDialog}
        onNewOrder={() => navigate(`/orders/new?customer=${customerId}`)}
        onOpenOrder={(o) => navigate(`/orders/${o.id}`)}
      />
    );
  }

  return (
    <Screen data-testid="customer-profile-page">
      <CommandBar
        title={customer.customer_name}
        subtitle={customer.firm_name}
        onBack={() => navigate('/customers')}
        actions={[
          canRecordPayments && {
            key: 'payment', label: 'Record Payment', icon: IndianRupee, tone: 'accent',
            onClick: () => openPaymentDialog(),
          },
          { key: 'new-order', label: 'New Order', icon: Package,
            onClick: () => navigate(`/orders/new?customer=${customerId}`) },
        ]}
      />

      <FactBand>
        <Fact label="Mobile">{customer.mobile_number}</Fact>
        <Fact label="Email">{customer.email}</Fact>
        <Fact label="GSTIN">{customer.gstin}</Fact>
        <Fact label="Customer since">{customer_since ? formatDate(customer_since) : '—'}</Fact>
        <Fact label="Billing">{customer.billing_address}</Fact>
        <Fact label="Delivery">{customer.delivery_address}</Fact>
        <Fact label="Orders">{total_orders || 0}</Fact>
        <Fact label="Open orders">{outstanding_orders?.length || 0}</Fact>
        {canSeeFinancials && (
          <>
            <Fact label="Billed">{money(financial_summary?.total_value)}</Fact>
            <Fact label="Paid">{money(financial_summary?.total_paid)}</Fact>
            <Fact label="Outstanding" valueClassName={(financial_summary?.outstanding || 0) > 0 ? 'font-medium text-red-600' : 'text-emerald-600'}>
              {money(financial_summary?.outstanding)}
            </Fact>
            <Fact label="Advance held">{money(financial_summary?.advance_balance)}</Fact>
          </>
        )}
      </FactBand>

      {canSeeFinancials && (
        <StatStrip
          stats={[
            { key: 'all', label: 'all orders', value: ordersTotal, tone: 'blue' },
            { key: 'unpaid', label: 'unpaid', value: aging.unpaid_count ?? '—', tone: 'danger' },
            { key: null, label: '0–30 days', value: money(aging['0_30'] ?? aging.days_0_30) },
            { key: null, label: '31–60', value: money(aging['31_60'] ?? aging.days_31_60), tone: 'warn' },
            { key: null, label: '60+', value: money(aging['60_plus'] ?? aging.days_60_plus), tone: 'danger' },
          ]}
          activeKey={paymentStatusFilter === 'all' ? 'all' : paymentStatusFilter}
          onSelect={(key) => {
            setPaymentStatusFilter(!key || key === 'all' ? 'all' : key);
            setOrdersPage(1);
          }}
        >
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setOrdersPage(1); }}>
            <SelectTrigger className="h-7 w-40 text-xs"><SelectValue placeholder="Any status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Any status</SelectItem>
              {['design', 'in_production', 'ready_for_dispatch', 'dispatched', 'delivered'].map(s => (
                <SelectItem key={s} value={s} className="text-xs">
                  {STATUS_COLORS[s]?.label || s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </StatStrip>
      )}

      <ScreenSplit
        railWidth={canSeeFinancials ? '20rem' : '0rem'}
        rail={canSeeFinancials ? (
          <Pane title="Payments received" aside={`${paymentsTotal}`} className="max-h-full">
            {paymentList.length === 0 ? (
              <p className="p-4 text-center text-xs text-slate-400">No payments recorded</p>
            ) : paymentList.map((p, i) => (
              <div key={p.id || i} className="flex items-start justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 last:border-b-0">
                <span className="min-w-0">
                  <span className="block truncate text-[12px] font-medium text-slate-700">
                    {p.order_display_id || p.order_id || 'On account'}
                  </span>
                  <span className="block truncate text-[10px] text-slate-400">
                    {p.mode}{p.reference_number ? ` · ${p.reference_number}` : ''}
                  </span>
                  <span className="block text-[10px] text-slate-400">
                    {p.date ? formatDateTime(p.date) : '—'}
                    {p.recorded_by_name ? ` · ${p.recorded_by_name}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] font-bold tabular-nums text-emerald-600">
                  {money(p.amount)}
                </span>
              </div>
            ))}
            {paymentsPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-2.5 py-1.5 text-[11px] text-slate-500">
                <button type="button" disabled={paymentsPage <= 1}
                  onClick={() => setPaymentsPage(p => p - 1)}
                  className="rounded px-1 hover:bg-slate-100 disabled:opacity-30">Prev</button>
                <span>{paymentsPage} / {paymentsPages}</span>
                <button type="button" disabled={paymentsPage >= paymentsPages}
                  onClick={() => setPaymentsPage(p => p + 1)}
                  className="rounded px-1 hover:bg-slate-100 disabled:opacity-30">Next</button>
              </div>
            )}
          </Pane>
        ) : null}
      >
        <Pane
          title="Order ledger"
          aside={`${orders.length} of ${ordersTotal}`}
          scroll={false}
          bodyClassName="flex flex-col"
          actions={ordersPages > 1 && (
            <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
              <button type="button" disabled={ordersPage <= 1} onClick={() => setOrdersPage(p => p - 1)}
                className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
              {ordersPage} / {ordersPages}
              <button type="button" disabled={ordersPage >= ordersPages} onClick={() => setOrdersPage(p => p + 1)}
                className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
            </span>
          )}
        >
          <DataGrid
            data-testid="customer-orders-table"
            columns={orderColumns}
            rows={orders}
            getRowId={(o) => o.id}
            onRowClick={(o) => navigate(`/orders/${o.id}`)}
            showTotals={canSeeFinancials && orders.length > 0}
            empty="No orders for this customer yet"
          />
        </Pane>
      </ScreenSplit>

      {canRecordPayments && <RecordPaymentPane payment={payment} />}
    </Screen>
  );
}
